import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { HOME_DASHBOARD_SLUG } from './dashboard';
import { useFileSystemDuplexer } from './use-file-system-duplexer';
import { queryClient, queryKeys } from './query-client';

// Client data layer for Symphony Packs — the shareable bundles of dashboards / agents /
// pulses / skills / knowledge / playbooks a partner hands to clients. Reads go through the
// /api/packs and /api/pack proxies (harness GET /v1/packs, GET /v1/pack?slug=); actions go
// through /api/pack/mint (harness POST /v1/pack/mint). Responses arrive as
// { result: ... } | { error: string } and are parsed tolerantly — a missing field degrades
// to a safe default rather than throwing.

export interface PackItemCounts {
  dashboards: number;
  agents: number;
  pulses: number;
  skills: number;
  knowledge: number;
  playbooks: number;
}

export interface PackSummary {
  slug: string;
  name: string;
  description: string;
  shared: boolean;
  disabled: boolean;
  quickShare: boolean;
  itemCounts: PackItemCounts;
  acceptedCount: number;
  sharedCount: number;
}

export interface PackContentDashboard {
  id: string;
  slug: string;
  title: string;
  /** True when the pack references a dashboard that no longer resolves. */
  missing: boolean;
}

export interface PackContentItem {
  path: string;
  name: string;
  summary: string;
  /** The item's full markdown (≤30KB) — shown in the quick-info modal. */
  content: string;
}

export interface PackContents {
  dashboards: PackContentDashboard[];
  agents: PackContentItem[];
  pulses: PackContentItem[];
  skills: PackContentItem[];
  knowledge: PackContentItem[];
  playbooks: PackContentItem[];
}

export interface PackRecipient {
  name: string;
  acceptedAt: string;
  installedCount: number;
  totalCount: number;
  stale: boolean;
}

export interface PackSenderCard {
  name: string;
  /** The RAW stored profile name ('' when unset) — `name` may be the account-name fallback,
   *  which an editor must never seed (saving would bake the fallback into the profile). */
  profileName?: string;
  icon: string;
  /** WixMP logo URL ('' when unset). Renders in place of the emoji icon when present. */
  logoUrl: string;
  tagline: string;
  description: string;
  url: string;
  businessName: string;
}

/** The partner's GLOBAL sender profile — shown on every pack they share. */
export interface PackProfile {
  name: string;
  icon: string;
  logoUrl: string;
  tagline: string;
  description: string;
  url: string;
}

/** Workspace/business-derived fallbacks used ONLY to pre-fill empty editor fields. */
export interface PackDefaults {
  name: string;
  tagline: string;
  description: string;
  url: string;
}

export interface PackDetail extends PackSummary {
  packMd: string;
  senderCard: PackSenderCard;
  /** Workspace/business-derived fallbacks — pre-fill empty profile-editor fields only. */
  defaults: PackDefaults;
  workspace: { name: string; summary: string } | null;
  contents: PackContents;
  recipients: PackRecipient[];
  inviteLink: string | null;
}

export const PACK_ITEM_TYPES = ['dashboards', 'agents', 'pulses', 'skills', 'knowledge', 'playbooks'] as const;
export type PackItemType = (typeof PACK_ITEM_TYPES)[number];

/** Order a pack's dashboards for display: the home/catch-up dashboard leads, the rest keep their
 *  manifest order (stable sort). */
export function orderPackDashboards(dashboards: PackContentDashboard[]): PackContentDashboard[] {
  return [...dashboards].sort(
    (a, b) => Number(b.slug === HOME_DASHBOARD_SLUG) - Number(a.slug === HOME_DASHBOARD_SLUG),
  );
}

// ---- tolerant parsing ------------------------------------------------------

const asRecord = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' ? (v as Record<string, unknown>) : {});
const asString = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);
const asBool = (v: unknown): boolean => v === true;
const asCount = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0);
const asArray = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

export function parseItemCounts(raw: unknown): PackItemCounts {
  const r = asRecord(raw);
  return {
    dashboards: asCount(r.dashboards),
    agents: asCount(r.agents),
    pulses: asCount(r.pulses),
    skills: asCount(r.skills),
    knowledge: asCount(r.knowledge),
    playbooks: asCount(r.playbooks),
  };
}

export function parsePackSummary(raw: unknown): PackSummary {
  const r = asRecord(raw);
  return {
    slug: asString(r.slug),
    name: asString(r.name),
    description: asString(r.description),
    shared: asBool(r.shared),
    disabled: asBool(r.disabled),
    quickShare: asBool(r.quickShare),
    itemCounts: parseItemCounts(r.itemCounts),
    acceptedCount: asCount(r.acceptedCount),
    sharedCount: asCount(r.sharedCount),
  };
}

function parseContentItem(raw: unknown): PackContentItem {
  const r = asRecord(raw);
  // Reference items (agents/pulses/playbooks/knowledge) travel as {kind, ref, label} — the reference
  // model suppresses their bodies — while custom skills carry {path, name, summary, content}. Fall back
  // name←label and path←ref so a reference row shows its label instead of blank (matches the native
  // `parsePackItem` fallback on both platforms).
  return {
    path: asString(r.path) || asString(r.ref),
    name: asString(r.name) || asString(r.label),
    summary: asString(r.summary),
    content: asString(r.content),
  };
}

function parseContentDashboard(raw: unknown): PackContentDashboard {
  const r = asRecord(raw);
  return { id: asString(r.id), slug: asString(r.slug), title: asString(r.title), missing: asBool(r.missing) };
}

export function parsePackProfile(raw: unknown): PackProfile {
  const r = asRecord(raw);
  return {
    name: asString(r.name),
    icon: asString(r.icon),
    logoUrl: asString(r.logoUrl),
    tagline: asString(r.tagline),
    description: asString(r.description),
    url: asString(r.url),
  };
}

export function parsePackDefaults(raw: unknown): PackDefaults {
  const r = asRecord(raw);
  return {
    name: asString(r.name),
    tagline: asString(r.tagline),
    description: asString(r.description),
    url: asString(r.url),
  };
}

function parseRecipient(raw: unknown): PackRecipient {
  const r = asRecord(raw);
  return {
    name: asString(r.name),
    acceptedAt: asString(r.acceptedAt),
    installedCount: asCount(r.installedCount),
    totalCount: asCount(r.totalCount),
    stale: asBool(r.stale),
  };
}

export function parsePackDetail(raw: unknown): PackDetail {
  const r = asRecord(raw);
  const senderCard = asRecord(r.senderCard);
  const workspace = r.workspace == null ? null : asRecord(r.workspace);
  const contents = asRecord(r.contents);
  return {
    ...parsePackSummary(raw),
    packMd: asString(r.packMd),
    senderCard: { ...parsePackProfile(senderCard), businessName: asString(senderCard.businessName), profileName: asString(senderCard.profileName) },
    defaults: parsePackDefaults(r.defaults),
    workspace: workspace ? { name: asString(workspace.name), summary: asString(workspace.summary) } : null,
    contents: {
      dashboards: asArray(contents.dashboards).map(parseContentDashboard),
      agents: asArray(contents.agents).map(parseContentItem),
      pulses: asArray(contents.pulses).map(parseContentItem),
      skills: asArray(contents.skills).map(parseContentItem),
      knowledge: asArray(contents.knowledge).map(parseContentItem),
      playbooks: asArray(contents.playbooks).map(parseContentItem),
    },
    recipients: asArray(r.recipients).map(parseRecipient),
    inviteLink: typeof r.inviteLink === 'string' && r.inviteLink ? r.inviteLink : null,
  };
}

// ---- fetching --------------------------------------------------------------

async function readEnvelope(res: Response, label: string): Promise<unknown> {
  if (!res.ok) {
    throw new Error(await readErrorMessage(res, label));
  }
  const data = (await res.json().catch(() => ({}))) as { result?: unknown; error?: unknown };
  if (data.error != null) {
    throw new Error(String(data.error));
  }
  return data.result ?? null;
}

interface PacksListing {
  packs: PackSummary[];
  profile: PackProfile;
  defaults: PackDefaults;
}

async function fetchPacks(): Promise<PacksListing> {
  const res = await apiFetch(`${API_BASE}/api/packs`, { credentials: 'include' });
  const result = asRecord(await readEnvelope(res, 'packs'));
  return {
    packs: asArray(result.packs).map(parsePackSummary),
    profile: parsePackProfile(result.profile),
    defaults: parsePackDefaults(result.defaults),
  };
}

async function fetchPack(slug: string): Promise<PackDetail | null> {
  const res = await apiFetch(`${API_BASE}/api/pack?slug=${encodeURIComponent(slug)}`, { credentials: 'include' });
  const result = await readEnvelope(res, 'pack');
  return result == null ? null : parsePackDetail(result);
}

export type PacksState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; packs: PackSummary[]; profile: PackProfile; defaults: PackDefaults };

/** Warm the packs list into the React Query cache on app mount, so the (slow) /api/packs read is
 *  already in flight — or done — by the time the user opens Packs. Best-effort; swallows errors. */
export function prefetchPacks(): void {
  void queryClient.prefetchQuery({ queryKey: queryKeys.packs, queryFn: fetchPacks }).catch(() => {});
}

export function usePacks(enabled = true): { state: PacksState; refresh: () => Promise<void> } {
  const query = useQuery({ queryKey: queryKeys.packs, queryFn: fetchPacks, enabled });
  // Packs live under the VFS `packs/` prefix, so Orion authoring or editing one from chat is a
  // file-system write — refetch on the same channel useWorkspaces/useMyAgents already ride, so
  // the list updates live while the user is still talking to the agent.
  useFileSystemDuplexer(() => {
    if (enabled) void query.refetch();
  });
  const state: PacksState = query.data
    ? { kind: 'ready', packs: query.data.packs, profile: query.data.profile, defaults: query.data.defaults }
    : query.status === 'error'
      ? { kind: 'error', message: query.error instanceof Error ? query.error.message : String(query.error) }
      : { kind: 'loading' };
  const refresh = () => queryClient.refetchQueries({ queryKey: queryKeys.packs }).then(() => undefined);
  return { state, refresh };
}

export type PackState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'missing' }
  | { kind: 'ready'; pack: PackDetail };

/** A dashboard the server couldn't resolve in time — its `resolvePackDashboards` call exceeded the
 *  pack endpoint's web-fn budget, so it came back with neither a slug nor a title (but not `missing`).
 *  A retry usually resolves it, which is why `usePack` polls while one is in this state. */
export function packHasUnresolvedDashboard(pack: PackDetail): boolean {
  return pack.contents.dashboards.some((d) => !d.missing && !d.slug && !d.title);
}

// While a dashboard is still resolving, poll so the row fills in on its own instead of sitting blank —
// bounded so a genuinely stuck reference can't poll forever.
const UNRESOLVED_POLL_MS = 2500;
const UNRESOLVED_POLL_LIMIT = 8;

export function usePack(slug: string): { state: PackState; refresh: () => Promise<void> } {
  const query = useQuery({
    queryKey: queryKeys.pack(slug),
    queryFn: () => fetchPack(slug),
    enabled: !!slug,
    // Retry a pack whose dashboards timed out resolving, so blank rows fill in on their own (bounded).
    refetchInterval: (data, q) =>
      data && packHasUnresolvedDashboard(data) && q.state.dataUpdateCount < UNRESOLVED_POLL_LIMIT ? UNRESOLVED_POLL_MS : false,
  });
  // Same live-update channel as usePacks — the pack screen re-renders as Orion edits it in chat,
  // instead of the user having to reload to see the agent's latest write.
  useFileSystemDuplexer(() => {
    if (slug) void query.refetch();
  });
  const state: PackState =
    query.data !== undefined
      ? query.data === null
        ? { kind: 'missing' }
        : { kind: 'ready', pack: query.data }
      : query.status === 'error'
        ? { kind: 'error', message: query.error instanceof Error ? query.error.message : String(query.error) }
        : { kind: 'loading' };
  const refresh = () => queryClient.refetchQueries({ queryKey: queryKeys.pack(slug) }).then(() => undefined);
  return { state, refresh };
}

/** The current detail for a slug — from the cache when fresh, otherwise fetched. The share
 *  flow uses this so a Share tapped on the LIST screen (which only holds a summary) can see
 *  `inviteLink` (first-share confirm) and the sender card. */
export function getPackDetail(slug: string): Promise<PackDetail | null> {
  return queryClient.fetchQuery({ queryKey: queryKeys.pack(slug), queryFn: () => fetchPack(slug) });
}

/** Drop the cached detail + list so the next read refetches (after a mint action). */
export async function invalidatePack(slug: string): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.pack(slug) }),
    queryClient.invalidateQueries({ queryKey: queryKeys.packs }),
  ]);
}

// ---- actions ---------------------------------------------------------------

export type PackMintAction = 'share' | 'disable' | 'enable' | 'delete';

export type PackMintResult =
  | { ok: true; link: string | null; disabled: boolean | null; deleted: boolean }
  | { ok: false; message: string };

/** POST /api/pack/mint. `share` answers { link }; `disable`/`enable` answer { disabled };
 *  `delete` answers { deleted }. A `{ error }` envelope (e.g. share validation failed) comes
 *  back as `ok: false` with the harness's message — the share flow shows it in the
 *  "Fix in chat" dialog. */
export async function mintPack(slug: string, action: PackMintAction): Promise<PackMintResult> {
  try {
    const res = await apiFetch(`${API_BASE}/api/pack/mint`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug, action }),
    });
    const result = asRecord(await readEnvelope(res, 'pack'));
    return {
      ok: true,
      link: typeof result.link === 'string' && result.link ? result.link : null,
      disabled: typeof result.disabled === 'boolean' ? result.disabled : null,
      deleted: result.deleted === true,
    };
  } catch (err: unknown) {
    return { ok: false, message: err instanceof Error ? err.message : String(err) };
  } finally {
    // The action may have changed shared/disabled/inviteLink — refetch both surfaces.
    void invalidatePack(slug);
  }
}

export type PackProfileResult = { ok: true; profile: PackProfile } | { ok: false; message: string };

/** POST /api/pack/profile — saves the partner's GLOBAL sender profile (every pack shows it).
 *  Invalidates the list and every cached pack detail, since all of them render the profile. */
export async function updatePackProfile(profile: PackProfile): Promise<PackProfileResult> {
  try {
    const res = await apiFetch(`${API_BASE}/api/pack/profile`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(profile),
    });
    const result = asRecord(await readEnvelope(res, 'pack profile'));
    return { ok: true, profile: parsePackProfile(result.profile) };
  } catch (err: unknown) {
    return { ok: false, message: err instanceof Error ? err.message : String(err) };
  } finally {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.packs }),
      queryClient.invalidateQueries({ queryKey: queryKeys.packAllDetails }),
    ]);
  }
}

// ---- accept (web pack-accept flow) -----------------------------------------

export interface PackAcceptParams {
  inviterMetaSiteId: string;
  slug: string;
  token: string;
}

export type PackAcceptOutcome =
  | { kind: 'success'; folder: string; itemCount: number; senderName: string; packName: string; conversationId: string }
  | { kind: 'unauthenticated' }
  | { kind: 'gone' }
  | { kind: 'invalid' };

const firstString = (v: string | string[] | undefined): string => (Array.isArray(v) ? (v[0] ?? '') : (v ?? ''));

/** Pull the invite params off the URL query. Accepts BOTH shapes:
 *  - path-style in `?deepLink=pack/<inviterMetaSiteId>/<slug>/<token>` (the jot link's payload), and
 *  - explicit `?inviter=<msid>&slug=<slug>&token=<token>` (a clean web link).
 *  Returns null when neither yields all three parts. Pure — the parse test drives it directly. */
export function parseAcceptParams(query: Record<string, string | string[] | undefined>): PackAcceptParams | null {
  const deepLink = firstString(query.deepLink);
  if (deepLink) {
    const segments = deepLink.split('/').filter(Boolean);
    const packIdx = segments.indexOf('pack');
    if (packIdx !== -1 && segments.length >= packIdx + 4) {
      const inviterMetaSiteId = segments[packIdx + 1];
      const slug = segments[packIdx + 2];
      const token = segments[packIdx + 3];
      if (inviterMetaSiteId && slug && token) return { inviterMetaSiteId, slug, token };
    }
    // A malformed deepLink falls through to the explicit params below.
  }
  const inviterMetaSiteId = firstString(query.inviter);
  const slug = firstString(query.slug);
  const token = firstString(query.token);
  if (inviterMetaSiteId && slug && token) return { inviterMetaSiteId, slug, token };
  return null;
}

/** POST /api/pack/accept. Success → the copied pack's folder + item count + who shared it.
 *  A 401 (no session) → `unauthenticated` (the screen offers sign-in); a 410 → `gone` (the pack
 *  is disabled/removed); any other failure → `invalid` (bad/expired link). Idempotent server-side. */
export async function acceptPack(params: PackAcceptParams): Promise<PackAcceptOutcome> {
  let res: Response;
  try {
    res = await apiFetch(`${API_BASE}/api/pack/accept`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });
  } catch {
    return { kind: 'invalid' };
  }
  if (res.status === 401) return { kind: 'unauthenticated' };
  if (res.status === 410) return { kind: 'gone' };
  if (!res.ok) return { kind: 'invalid' };
  const data = (await res.json().catch(() => ({}))) as { result?: unknown; error?: unknown };
  if (data.error != null || data.result == null) return { kind: 'invalid' };
  const r = asRecord(data.result);
  return {
    kind: 'success',
    folder: asString(r.folder),
    itemCount: asCount(r.itemCount),
    senderName: asString(r.senderName),
    packName: asString(r.packName),
    conversationId: asString(r.conversationId),
  };
}

export interface PackExport {
  filename: string;
  markdown: string;
}

/** GET /api/pack/export?slug= — the pack as a downloadable markdown file. Throws on failure. */
export async function exportPack(slug: string): Promise<PackExport> {
  const res = await apiFetch(`${API_BASE}/api/pack/export?slug=${encodeURIComponent(slug)}`, { credentials: 'include' });
  const result = asRecord(await readEnvelope(res, 'pack export'));
  return { filename: asString(result.filename), markdown: asString(result.markdown) };
}

// Same shape as the one in lib/pulses.ts — keeps a non-JSON response (e.g. a 404 HTML page
// from the dev proxy hitting an undeployed route) out of the user-facing error string.
async function readErrorMessage(res: Response, label: string): Promise<string> {
  const fallback = `Couldn't load ${label} (${res.status})`;
  const text = await res.text().catch(() => '');
  if (!text) return fallback;
  if (text.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(text) as { message?: string; error?: string };
      const msg = parsed.message || parsed.error;
      if (msg) return msg;
    } catch {
      // fall through to status-only fallback
    }
  }
  return fallback;
}
